import { z } from 'zod';
import { SegmentedMessage } from 'sms-segments-calculator';

/**
 * The number that matters is not the segment count but what fits in it: one `ą`
 * anywhere switches the message to UCS-2, where a segment holds 67 characters
 * rather than 153. At three segments a Polish message ran out at 201
 * characters, which is shorter than a confirmation with a date and an address.
 * Six gives it 402, nine 603. Every segment is billed as its own SMS.
 */
export function smsMaxSegments({ longerSms }: { longerSms: boolean }) {
  return longerSms ? 9 : 6;
}

export const TELNYX_RECIPIENT_OPTED_OUT_ERROR_CODE = '40300';

export const smsProviderStatusSchema = z.enum([
  'queued',
  'sending',
  'sent',
  'delivered',
  'delivery_unconfirmed',
  'sending_failed',
  'delivery_failed',
]);

/**
 * Everything a message's status can say.
 *
 * The provider's own vocabulary plus the three states that are ours: `received`
 * on anything inbound, `pending` between accepting a send and hearing back, and
 * `submission_failed` when it never reached the provider at all.
 *
 * One home for the union, because the table's column type and the label the
 * desk reads have to agree on it.
 */
export const smsStatusSchema = z.union([
  smsProviderStatusSchema,
  z.enum(['received', 'pending', 'submission_failed']),
]);

export type SmsStatus = z.infer<typeof smsStatusSchema>;

export const telnyxMessagingErrorSchema = z.object({
  code: z.string().optional(),
  title: z.string().optional(),
  detail: z.string().optional(),
});

export const telnyxMessagingErrorResponseSchema = z.object({
  errors: z.array(telnyxMessagingErrorSchema).optional(),
});

export const smsProviderResultSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('accepted'),
    providerMessageId: z.string(),
    status: smsProviderStatusSchema,
  }),
  z.object({
    kind: z.literal('failed'),
    errorCode: z.string().optional(),
    errorTitle: z.string(),
    errorDetail: z.string().optional(),
  }),
]);

export type SmsProviderStatus = z.infer<typeof smsProviderStatusSchema>;

export function createSmsTextSchema(
  maxSegments: number,
  messages = {
    emptyMessage: 'SMS message cannot be empty',
    tooLongMessage: `SMS message cannot exceed ${maxSegments} segments`,
  },
) {
  return z.string().superRefine((text, ctx) => {
    if (!text.trim()) {
      ctx.addIssue({ code: 'custom', message: messages.emptyMessage });
      return;
    }

    if (getSmsSegmentCount(text) > maxSegments) {
      ctx.addIssue({ code: 'custom', message: messages.tooLongMessage });
    }
  });
}

export function getSmsSegmentCount(text: string) {
  return text ? new SegmentedMessage(text).segmentsCount : 0;
}
